import { useMemo, useState, useEffect } from "react";
import { getBookRegisteredDate } from "../utils/format";
import "./BookOrbitTrail.css";

function getYears(books, userId) {
  const years = new Set();
  books.forEach((b) => {
    const y = getBookRegisteredDate(b, userId).slice(0, 4);
    if (y.length === 4) years.add(y);
  });
  return [...years].sort().reverse();
}

// Seeded random for stable positions per book id
function seededRand(seed) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = Math.imul(31, h) + seed.charCodeAt(i) | 0;
  }
  return ((h >>> 0) % 1000) / 1000;
}

function layoutOrbit(books, userId) {
  const sorted = [...books].sort((a, b) => {
    const dA = getBookRegisteredDate(a, userId);
    const dB = getBookRegisteredDate(b, userId);
    return dB.localeCompare(dA);
  });

  const completedBooks = sorted.filter(b => b.completedBy && b.completedBy[userId]);
  const readingBooks = sorted.filter(b => !b.completedBy || !b.completedBy[userId]);

  // Group completed books by date
  const groups = [];
  let prevDate = null;
  completedBooks.forEach((book) => {
    const date = getBookRegisteredDate(book, userId).slice(0, 10) || "unknown";
    if (date !== prevDate) {
      groups.push({ date, books: [book] });
      prevDate = date;
    } else {
      groups[groups.length - 1].books.push(book);
    }
  });

  const nodes = [];
  let nodeIdx = 0;
  let currentY = 60;
  const INTER_GROUP = 100;
  const SUB_ROW = 70;
  const MAX_PER_ROW = 2;

  // Layout completed books on date lines, track group centers
  const groupCenters = [];
  const groupBubbles = [];

  groups.forEach((group, gIdx) => {
    const count = group.books.length;
    const subRows = Math.ceil(count / MAX_PER_ROW);
    const startIdx = nodes.length;

    group.books.forEach((book, j) => {
      const subRow = Math.floor(j / MAX_PER_ROW);
      const posInSubRow = j % MAX_PER_ROW;
      const booksInRow = Math.min(MAX_PER_ROW, count - subRow * MAX_PER_ROW);

      const y = currentY + subRow * SUB_ROW;

      let x;
      if (booksInRow === 1) {
        x = 50 + 28 * Math.sin((gIdx + subRow) * 1.2);
      } else {
        x = posInSubRow === 0 ? 28 : 72;
      }

      // 한 줄에 둘이면 라벨을 아래로 내린다. 28% / 72% 에 놓이는데
      // 라벨 폭이 120px 라, 안쪽으로 붙이면 서로 부딪히고 바깥으로
      // 붙이면 화면을 넘는다. 아래로 내리면 둘 다 피한다.
      const labelBelow = booksInRow > 1;
      const labelLeft = !labelBelow && x > 50;
      nodes.push({ book, x, y, completed: true, labelLeft, labelBelow, idx: nodeIdx, groupSize: booksInRow });
      nodeIdx++;
    });

    // Group center for edge connections
    const groupNodes = nodes.slice(startIdx);
    const centerX = groupNodes.reduce((a, n) => a + n.x, 0) / count;
    const centerY = currentY + (subRows - 1) * SUB_ROW / 2;
    groupCenters.push({ x: centerX, y: centerY, count });

    // Group bubble for 2+ books
    if (count >= 2) {
      const xs = groupNodes.map(n => n.x);
      const ys = groupNodes.map(n => n.y);
      groupBubbles.push({
        centerX: (Math.min(...xs) + Math.max(...xs)) / 2,
        centerY: (Math.min(...ys) + Math.max(...ys)) / 2,
        spanX: Math.max(...xs) - Math.min(...xs),
        spanY: Math.max(...ys) - Math.min(...ys),
        idx: groupBubbles.length,
      });
    }

    currentY += (subRows - 1) * SUB_ROW + INTER_GROUP;
  });

  const completedMapBottom = currentY > 60 ? currentY - INTER_GROUP : 60;

  // 읽는 중인 책은 빈 곳에 놓는다.
  //
  // 예전에는 이미 놓인 노드를 보지 않고 무작위로 떨어뜨려서, 완독한
  // 책 위에 그대로 겹쳤다. 무작위로 몇 번 뽑는 방식도 빈자리를 잘
  // 못 찾는다 — 격자를 전부 훑어 가장 멀리 떨어지는 칸을 고른다.
  // 그래도 자리가 없으면 지도 아래로 밀어낸다.

  /** 노드 지름 44px + 라벨이 지나갈 여유 */
  const MIN_GAP = 78;
  /** x 는 %, y 는 px 라 단위를 맞춘다 (지도 폭을 350px 로 본다) */
  const PX_PER_PCT = 3.5;

  const gapTo = (x, y) => {
    let g = Infinity;
    for (const n of nodes) {
      g = Math.min(g, Math.hypot((x - n.x) * PX_PER_PCT, y - n.y));
    }
    return g;
  };

  let overflowY = completedMapBottom + 90;
  readingBooks.forEach((book) => {
    const xs = [16, 28, 40, 52, 64, 76, 84];
    let best = null, bestGap = -1;
    for (const x of xs) {
      for (let y = 40; y <= completedMapBottom + 40; y += 34) {
        const g = gapTo(x, y);
        if (g > bestGap) { bestGap = g; best = { x, y }; }
      }
    }
    // 지도 안에 놓을 자리가 없으면 아래로 이어 붙인다.
    // 겹쳐 놓느니 지도가 길어지는 편이 낫다.
    if (bestGap < MIN_GAP) {
      best = { x: 30 + seededRand(String(book.id)) * 40, y: overflowY };
      overflowY += 84;
    }
    nodes.push({ book, ...best, completed: false, labelLeft: best.x > 50, labelBelow: false, idx: nodeIdx, groupSize: 0 });
    nodeIdx++;
  });

  // Edges: connect group centers (not individual books)
  const edges = [];
  for (let i = 0; i < groupCenters.length - 1; i++) {
    const from = groupCenters[i];
    const to = groupCenters[i + 1];
    const midX = (from.x + to.x) / 2 + (edges.length % 2 === 0 ? 10 : -10);
    const midY = (from.y + to.y) / 2;
    edges.push({ from, to, midX, midY, idx: edges.length });
  }

  const allY = nodes.map(n => n.y);
  const mapHeight = allY.length === 0 ? 200 : Math.max(...allY) + 80;
  return { nodes, edges, groupBubbles, mapHeight };
}

const BookOrbitTrail = ({ books, userId, onBookClick, onExport, exportableYears }) => {
  // books 가 undefined 일 때 매 렌더마다 새 배열이 생겨 아래 useMemo 들이 전부 무효화된다.
  // (CI 빌드에서 경고가 에러로 승격되어 배포를 막는다)
  const allBooks = useMemo(() => books || [], [books]);
  const years = useMemo(() => getYears(allBooks, userId), [allBooks, userId]);
  const [selectedYear, setSelectedYear] = useState("all");

  const filteredBooks = useMemo(() => {
    if (selectedYear === "all") return allBooks;
    return allBooks.filter((b) => getBookRegisteredDate(b, userId).startsWith(selectedYear));
  }, [allBooks, selectedYear, userId]);

  const { nodes, edges, groupBubbles, mapHeight } = useMemo(
    () => layoutOrbit(filteredBooks, userId),
    [filteredBooks, userId]
  );

  // 하나씩 차례로 나타나게 한다.
  //
  // 간격을 300ms 로 고정해 두었더니 책이 30권이면 노드 30 + 선 29 =
  // 59단계, 18초가 걸렸다. '전체' 를 누르면 한참 빈 화면이라 고장으로
  // 보였다. 전체 시간을 묶고 간격을 거기서 나눈다.
  const [revealed, setRevealed] = useState(0);
  useEffect(() => {
    const total = nodes.length + edges.length;
    if (total === 0) { setRevealed(0); return; }
    setRevealed(0);
    // 항목이 많아도 2.4초 안에 다 나타난다. 적으면 한 단계씩 음미할
    // 만큼 느리게 — 다만 너무 느리지 않게 상한을 둔다.
    const step = Math.max(24, Math.min(220, 2400 / total));
    let count = 0;
    const timer = setInterval(() => {
      count++;
      setRevealed(count);
      if (count >= total) clearInterval(timer);
    }, step);
    return () => clearInterval(timer);
  }, [nodes.length, edges.length, selectedYear]);

  if (allBooks.length === 0) {
    return (
      <div className="orbit-trail">
        <div className="orbit-empty">
          <div className="orbit-empty-icon">🛸</div>
          <div className="orbit-empty-text">
            아직 독서 궤적이 없어요.<br />
            책을 등록하면 나만의 궤도가 만들어져요!
          </div>
        </div>
      </div>
    );
  }

  const completedCount = nodes.filter((n) => n.completed).length;
  const readingCount = nodes.length - completedCount;

  return (
    <div className="orbit-trail">
      {/* 연도가 한 종류여도 보여준다. 숨겼더니 내보내기 버튼은
          '연도를 고르면' 이라 하는데 고를 칩이 없었다. 2026 만 있는
          사람이 다섯이다. */}
      {years.length > 0 && (
        <div className="orbit-year-tabs">
          <button
            className={`orbit-year-tab${selectedYear === "all" ? " active" : ""}`}
            onClick={() => setSelectedYear("all")}
          >
            전체
          </button>
          {years.map((y) => (
            <button
              key={y}
              className={`orbit-year-tab${selectedYear === y ? " active" : ""}`}
              onClick={() => setSelectedYear(y)}
            >
              {y}
            </button>
          ))}
        </div>
      )}

      {/* 내보내기를 궤도 안에 둔다. 연도 선택이 두 군데 있으면 어느 쪽이
          이미지에 반영되는지 알 수 없다. 지금 보고 있는 해를 내보낸다.
          '전체' 는 한 해의 흐름을 그리는 그림과 맞지 않아 막는다. */}
      {onExport && (() => {
        // 연도가 하나뿐이면 '전체' 와 그 해가 같은 뜻이다. 고르라고 할 게 없다.
        const only = years.length === 1 ? years[0] : null;
        const year = selectedYear === "all" ? only : selectedYear;
        const isAll = !year;
        const canExport = !!year && exportableYears?.includes(Number(year));
        return (
          <div className="orbit-export">
            <button
              className="btn-year-trail"
              onClick={() => canExport && onExport(Number(year))}
              disabled={!canExport}
            >
              🌠 {isAll ? "궤적" : `${year} 궤적`} 이미지로 내보내기
            </button>
            {isAll && <div className="orbit-export-hint">연도를 고르면 내보낼 수 있어요</div>}
            {!isAll && !canExport && (
              <div className="orbit-export-hint">
                {year}년에는 감정 좌표를 찍은 완독 책이 3권 미만이라 선을 그릴 수 없어요
              </div>
            )}
          </div>
        );
      })()}

      {nodes.length === 0 ? (
        <div className="orbit-empty">
          <div className="orbit-empty-icon">🛸</div>
          <div className="orbit-empty-text">
            {selectedYear}년에 등록된 책이 없어요.
          </div>
        </div>
      ) : (
        <>
          <div className="orbit-map" style={{ height: mapHeight }}>
            <svg
              className="orbit-svg"
              viewBox={`0 0 100 ${mapHeight}`}
              preserveAspectRatio="none"
              style={{ height: mapHeight }}
            >
              <defs>
                <linearGradient id="orbitGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="rgba(123,108,212,.6)" />
                  <stop offset="100%" stopColor="rgba(75,168,160,.5)" />
                </linearGradient>
                <filter id="orbitGlow">
                  <feGaussianBlur stdDeviation="1.2" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>
              {edges.map((edge) => {
                const revealIdx = edge.idx + edge.idx + 1;
                if (revealed < revealIdx) return null;
                const d = `M ${edge.from.x} ${edge.from.y} Q ${edge.midX} ${edge.midY} ${edge.to.x} ${edge.to.y}`;
                return (
                  <path
                    key={`ep-${edge.idx}`}
                    d={d}
                    fill="none"
                    stroke="url(#orbitGrad)"
                    strokeWidth=".35"
                    filter="url(#orbitGlow)"
                    className="orbit-path"
                    style={{ animationDelay: `${revealIdx * 0.3}s` }}
                  />
                );
              })}
            </svg>

            {groupBubbles.map((gb) => (
              <div
                key={`gb-${gb.idx}`}
                className="orbit-group-bubble"
                style={{
                  left: `${gb.centerX}%`,
                  top: gb.centerY,
                  width: `${gb.spanX}%`,
                  height: gb.spanY,
                }}
              />
            ))}

            {nodes.map((node) => {
              const revealIdx = node.idx + Math.min(node.idx, edges.length);
              const visible = revealed >= revealIdx;
              const dateStr = getBookRegisteredDate(node.book, userId);
              const displayDate = dateStr.slice(0, 10);

              return (
                <div
                  key={`on-${node.book.id}`}
                  className={`orbit-node${visible ? " visible" : ""}${node.labelLeft ? " label-left" : ""}${node.labelBelow ? " label-below" : ""}${node.completed ? " completed" : " reading"}`}
                  style={{ left: `${node.x}%`, top: node.y }}
                  onClick={() => onBookClick(node.book.id)}
                >
                  <div className="orbit-node-orb">
                    <div className={`orbit-node-ring ${node.completed ? "completed" : "reading"}`} />
                    {node.book.cover ? (
                      <img className="orbit-node-cover" src={node.book.cover} alt={node.book.title} />
                    ) : (
                      <span>{node.book.emoji || "📖"}</span>
                    )}
                    <span className={`orbit-node-badge ${node.completed ? "badge-completed" : "badge-reading"}`}>
                      {node.completed ? "✅" : "📖"}
                    </span>
                  </div>
                  <div className="orbit-node-label">
                    <div className="orbit-node-label-title">{node.book.title}</div>
                    {displayDate && <div className="orbit-node-label-date">{displayDate}</div>}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="orbit-info">
            {nodes.length}권 · 완독 {completedCount} · 읽는 중 {readingCount}
          </div>
        </>
      )}
    </div>
  );
};

export default BookOrbitTrail;
